import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import ANALYTICS_CONFIG, { isAnalyticsEnabled } from '../../config/analytics.config';
import { logger } from "../utils/logger";
// import { logger } from "../utils/logger";
interface UseGoogleAnalyticsOptions {
    measurementId?: string;
    debugMode?: boolean;
}
/**
 * Custom hook for Google Analytics 4 integration with React Router
 *
 * Features:
 * - Loads GA4 script asynchronously
 * - Configures privacy-friendly settings
 * - Tracks page views on route changes
 * - Supports enhanced measurement (scroll, outbound clicks, etc.)
 * - User-ID tracking for authenticated users
 */
export const useGoogleAnalytics = ({ measurementId = ANALYTICS_CONFIG.measurementId, debugMode = ANALYTICS_CONFIG.debugMode }: UseGoogleAnalyticsOptions = {}) => {
    const location = useLocation();
    const scrollThresholdsTracked = useRef<Set<number>>(new Set());
    const lastScrollDepth = useRef(0);
    const gaReadyRef = useRef(false);
    const pendingScrollEventsRef = useRef<Set<number>>(new Set());
    const getOrCreateClientId = useCallback((): string | null => {
        if (typeof document === 'undefined') {
            return null;
        }
        const gaCookie = document.cookie
            .split(';')
            .map(cookie => cookie.trim())
            .find(cookie => cookie.startsWith('_ga='));
        if (gaCookie) {
            const value = gaCookie.split('=')[1];
            if (value) {
                const segments = value.split('.');
                if (segments.length >= 4) {
                    return `${segments[2]}.${segments[3]}`;
                }
            }
        }
        if (typeof window !== 'undefined' && window.localStorage) {
            try {
                const stored = window.localStorage.getItem('apt_anon_ga_cid');
                if (stored) {
                    return stored;
                }
                const generated = `${Math.floor(Math.random() * 1e10)}.${Math.floor(Date.now() / 1000)}`;
                window.localStorage.setItem('apt_anon_ga_cid', generated);
                return generated;
            }
            catch (error) {
                if (debugMode) {
                    logger.error('GA client ID localStorage error:', error, {});
                }
            }
        }
        return `${Math.floor(Math.random() * 1e10)}.${Math.floor(Date.now() / 1000)}`;
    }, [debugMode]);
    const sendScrollBeacon = useCallback((percent: number): boolean => {
        if (typeof window === 'undefined' || typeof navigator === 'undefined' || !measurementId) {
            return false;
        }
        const clientId = getOrCreateClientId();
        if (!clientId) {
            return false;
        }
        const params = new URLSearchParams({
            v: '2',
            tid: measurementId,
            cid: clientId,
            en: 'scroll',
            'epn.percent_scrolled': String(percent),
            dl: window.location.href,
            dt: document.title,
            ul: navigator.language || 'en-us',
            sr: `${window.screen.width}x${window.screen.height}`,
            _s: '1'
        });
        if (document.referrer) {
            params.set('dr', document.referrer);
        }
        const payload = params.toString();
        if (typeof navigator.sendBeacon === 'function') {
            const sent = navigator.sendBeacon('https://www.google-analytics.com/g/collect', payload);
            if (sent) {
                if (debugMode) {
                    logger.debug('📨 GA scroll beacon sent via sendBeacon', { "arg0": { percent, payload } });
                }
                return true;
            }
        }
        if (typeof fetch === 'function') {
            fetch('https://www.google-analytics.com/g/collect', {
                method: 'POST',
                body: payload,
                keepalive: true,
                headers: {
                    'Content-Type': 'text/plain'
                }
            }).catch(error => {
                if (debugMode) {
                    logger.error('GA scroll fetch fallback failed:', error, {});
                }
            });
            if (debugMode) {
                logger.debug('📨 GA scroll beacon sent via fetch fallback', { "arg0": { percent } });
            }
            return true;
        }
        return false;
    }, [debugMode, getOrCreateClientId, measurementId]);
    const dispatchScrollEvent = useCallback((percent: number) => {
        if (!measurementId) {
            return;
        }
        const sent = sendScrollBeacon(percent);
        if (!sent && typeof window !== 'undefined' && typeof window.gtag === 'function') {
            window.gtag('event', 'scroll', {
                percent_scrolled: percent,
                page_location: window.location.href,
                transport_type: 'beacon'
            });
        }
    }, [measurementId, sendScrollBeacon]);
    const flushPendingScrollEvents = useCallback(() => {
        if (pendingScrollEventsRef.current.size === 0) {
            return;
        }
        pendingScrollEventsRef.current.forEach(percent => {
            dispatchScrollEvent(percent);
        });
        pendingScrollEventsRef.current.clear();
    }, [dispatchScrollEvent]);
    // Initialize GA4 (only once)
    // NOTE: GA script is loaded in index.html, this hook only handles configuration
    useEffect(() => {
        // SSR safety check
        if (typeof window === 'undefined') {
            return;
        }
        // Check if analytics is enabled
        if (!isAnalyticsEnabled()) {
            logger.warn('⚠️ Google Analytics is disabled');
            return;
        }
        // Check if already initialized to prevent duplicate config
        const alreadyInitialized = document.querySelector(`meta[name="ga-initialized"][content="${measurementId}"]`);
        if (alreadyInitialized) {
            gaReadyRef.current = true;
            flushPendingScrollEvents();
            // Already initialized, skip configuration
            return;
        }
        // Check if script already exists (should be loaded from index.html)
        const existingScript = document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${measurementId}"]`);
        if (!existingScript) {
            // Fallback: Load Google Analytics script if not loaded from index.html
            logger.warn('⚠️ GA script not found in index.html, loading dynamically');
            const script = document.createElement('script');
            script.async = true;
            script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
            document.head.appendChild(script);
        }
        // Initialize gtag if not already defined (should be defined in index.html)
        if (!window.gtag) {
            window.dataLayer = window.dataLayer || [];
            window.gtag = function () {
                window.dataLayer.push(arguments);
            };
        }
        // Wait for gtag to be ready before configuring
        const configureGA = () => {
            if (!window.gtag) {
                setTimeout(configureGA, 100);
                return;
            }
            // Debug mode configuration
            const finalDebugMode = debugMode; // Only enable debug mode when explicitly requested
            
            // Configure gtag with centralized settings (SINGLE CONFIGURATION POINT)
            window.gtag('js', new Date());
            window.gtag('config', measurementId, {
                send_page_view: false, // CRITICAL: Disable automatic page views, we handle manually
                page_title: document.title,
                page_location: window.location.href,
                debug_mode: finalDebugMode, // Only enabled when explicitly requested
                transport_type: 'beacon',
                // Privacy settings from config
                allow_enhanced_conversions: ANALYTICS_CONFIG.allowEnhancedConversions,
                allow_google_signals: ANALYTICS_CONFIG.allowGoogleSignals,
                allow_ad_personalization_signals: ANALYTICS_CONFIG.allowAdPersonalization,
                anonymize_ip: ANALYTICS_CONFIG.anonymizeIp,
                cookie_flags: ANALYTICS_CONFIG.cookieFlags,
                cookie_domain: ANALYTICS_CONFIG.cookieDomain,
                cookie_expires: ANALYTICS_CONFIG.cookieExpires,
                // Custom dimensions mapping
                custom_map: ANALYTICS_CONFIG.customDimensions,
            });
            // Mark as initialized to prevent re-configuration
            const meta = document.createElement('meta');
            meta.name = 'ga-initialized';
            meta.content = measurementId;
            document.head.appendChild(meta);
            logger.api('✅ Google Analytics 4 configured:', {
                measurementId,
                debugMode: finalDebugMode,
                endpoint: finalDebugMode ? 'debug' : 'production',
                enhancedMeasurement: ANALYTICS_CONFIG.enableEnhancedMeasurement,
            });
            
            gaReadyRef.current = true;
            flushPendingScrollEvents();
        };
        // Start configuration
        configureGA();
        return () => {
            // Cleanup is handled by React Router's location changes
        };
    }, [flushPendingScrollEvents, measurementId, debugMode]);

    /**
     * Override index.html bootstrap: meta ga-initialized skips full hook config, so apply
     * measurement policy + custom_map from ANALYTICS_CONFIG (including VITE_GA_* env) after gtag exists.
     */
    useEffect(() => {
        if (typeof window === 'undefined' || !isAnalyticsEnabled() || !measurementId) {
            return;
        }
        let cancelled = false;
        let intervalId: ReturnType<typeof setInterval> | null = null;
        const applyMeasurementPolicy = (): boolean => {
            if (cancelled || typeof window.gtag !== 'function') {
                return false;
            }
            window.gtag('config', measurementId, {
                allow_google_signals: ANALYTICS_CONFIG.allowGoogleSignals,
                allow_ad_personalization_signals: ANALYTICS_CONFIG.allowAdPersonalization,
                allow_enhanced_conversions: ANALYTICS_CONFIG.allowEnhancedConversions,
                anonymize_ip: ANALYTICS_CONFIG.anonymizeIp,
                custom_map: { ...ANALYTICS_CONFIG.customDimensions },
            });
            return true;
        };
        if (applyMeasurementPolicy()) {
            return () => {
                cancelled = true;
            };
        }
        const deadline = Date.now() + 30000;
        intervalId = window.setInterval(() => {
            if (cancelled) {
                if (intervalId !== null) {
                    clearInterval(intervalId);
                    intervalId = null;
                }
                return;
            }
            if (Date.now() > deadline || applyMeasurementPolicy()) {
                if (intervalId !== null) {
                    clearInterval(intervalId);
                    intervalId = null;
                }
            }
        }, 100);
        return () => {
            cancelled = true;
            if (intervalId !== null) {
                clearInterval(intervalId);
            }
        };
    }, [measurementId]);

    // Track page views on route changes (React Router integration)
    useEffect(() => {
        // SSR safety check
        if (typeof window === 'undefined' || !window.gtag || !isAnalyticsEnabled()) {
            return;
        }
        // Reset scroll tracking for new page
        scrollThresholdsTracked.current.clear();
        lastScrollDepth.current = 0;
        // Small delay to ensure page title is updated
        const timeoutId = setTimeout(() => {
            window.gtag('event', 'page_view', {
                page_title: document.title,
                page_location: window.location.href,
                page_path: location.pathname + location.search + location.hash,
                page_referrer: document.referrer,
            });
        }, 100);
        return () => clearTimeout(timeoutId);
    }, [location.pathname, location.search, location.hash]);
    // Enhanced Measurement: Scroll Depth Tracking
    useEffect(() => {
        if (!ANALYTICS_CONFIG.enhancedMeasurement.scrollTracking || !isAnalyticsEnabled()) {
            return;
        }
        const handleScroll = () => {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
            const scrollPercent = Math.round((scrollTop / scrollHeight) * 100);
            // Track each threshold only once per page
            ANALYTICS_CONFIG.scrollDepthThresholds.forEach(threshold => {
                if (scrollPercent >= threshold && !scrollThresholdsTracked.current.has(threshold)) {
                    scrollThresholdsTracked.current.add(threshold);
                    if (!gaReadyRef.current) {
                        pendingScrollEventsRef.current.add(threshold);
                        return;
                    }
                    dispatchScrollEvent(threshold);
                }
            });
            lastScrollDepth.current = Math.max(lastScrollDepth.current, scrollPercent);
        };
        // Debounce scroll events for performance
        let scrollTimeout: NodeJS.Timeout;
        const debouncedHandleScroll = () => {
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(handleScroll, 100);
        };
        window.addEventListener('scroll', debouncedHandleScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', debouncedHandleScroll);
            clearTimeout(scrollTimeout);
        };
    }, [dispatchScrollEvent, location.pathname]);
    // Enhanced Measurement: Outbound Link Tracking
    useEffect(() => {
        if (!ANALYTICS_CONFIG.enhancedMeasurement.outboundClicks || !isAnalyticsEnabled()) {
            return;
        }
        const handleClick = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            
            // Skip Material-UI dropdown components to avoid interfering with click-away detection
            const isMuiDropdown = target.closest('.MuiSelect-root, .MuiPopover-root, .MuiMenu-root, [role="listbox"], .MuiPaper-root[role="presentation"]') !== null;
            if (isMuiDropdown) {
                return;
            }

            const link = target.closest('a');
            if (link && link.href) {
                const currentDomain = window.location.hostname;
                const linkDomain = new URL(link.href).hostname;
                // Check if it's an outbound link
                if (linkDomain !== currentDomain && linkDomain !== '') {
                    const eventId = `click_outbound_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                    window.gtag?.('event', 'click', {
                        event_category: 'outbound',
                        event_label: link.href,
                        event_id: eventId, // For deduplication
                        transport_type: 'beacon',
                    });
                }
            }
        };
        document.addEventListener('click', handleClick, true);
        return () => {
            document.removeEventListener('click', handleClick, true);
        };
    }, []);
    // Enhanced Measurement: Form Start Tracking
    useEffect(() => {
        if (!ANALYTICS_CONFIG.enhancedMeasurement.formInteractions || !isAnalyticsEnabled()) {
            return;
        }
        const trackedForms = new Set<string>();
        const handleFocus = (event: FocusEvent) => {
            const target = event.target as HTMLElement;
            // Check if it's a form input field
            if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
                const form = target.closest('form');
                if (form) {
                    const formId = form.id || form.getAttribute('name') || 'unknown';
                    const formKey = `${formId}_${window.location.pathname}`;
                    // Only track once per form per page
                    if (!trackedForms.has(formKey)) {
                        trackedForms.add(formKey);
                        const eventId = `form_start_${formId}_${Date.now()}`;
                        window.gtag?.('event', ANALYTICS_CONFIG.events.formStart, {
                            form_id: formId,
                            form_name: form.getAttribute('name') || formId,
                            form_location: window.location.pathname,
                            event_id: eventId, // For deduplication
                        });
                    }
                }
            }
        };
        document.addEventListener('focusin', handleFocus, true);
        return () => {
            document.removeEventListener('focusin', handleFocus, true);
        };
    }, [location.pathname]);
    
    // Enhanced Measurement: File Download Tracking
    useEffect(() => {
        if (!ANALYTICS_CONFIG.enhancedMeasurement.fileDownloads || !isAnalyticsEnabled()) {
            return;
        }
        const handleClick = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            
            // Skip Material-UI dropdown components to avoid interfering with click-away detection
            const isMuiDropdown = target.closest('.MuiSelect-root, .MuiPopover-root, .MuiMenu-root, [role="listbox"], .MuiPaper-root[role="presentation"]') !== null;
            if (isMuiDropdown) {
                return;
            }

            const link = target.closest('a');
            if (link && link.href) {
                const fileExtensions = /\.(pdf|xlsx?|docx?|txt|rtf|csv|zip|rar|7z|exe|dmg|pkg|deb)$/i;
                if (fileExtensions.test(link.href)) {
                    window.gtag?.('event', ANALYTICS_CONFIG.events.fileDownload, {
                        file_name: link.href.split('/').pop(),
                        file_extension: link.href.split('.').pop(),
                        link_url: link.href,
                    });
                }
            }
        };
        document.addEventListener('click', handleClick, true);
        return () => {
            document.removeEventListener('click', handleClick, true);
        };
    }, []);
};
